import { fetchCurrentUserThunk } from '@/redux/slices/currentUserSlice'
import { useEffect, useState } from 'react'
import { ActivityIndicator, Platform, Pressable, ScrollView, View, Text } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { getOfferings, purchasePackage, restorePurchases, PackageIdentifier, EntitlementIdentifier } from '@/lib/revenuecat'
import type { PurchasesPackage } from 'react-native-purchases'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import getBillingAccountPlanInfo from '@/redux/utils/getBillingAccountPlanInfo'
import { useRouter } from 'expo-router'
import { PLANS, isPlanCodeGtOther } from '@/lib/payment/plans'
import { PlanCode, SubscriptionSource } from '@/typings/domain'
import PricingOption from '@/modules/iosPaywall/PricingOption'
import { IconSymbol } from '@/components/ui/IconSymbol'
import Alert from '@/components/Alert'


const pro2EntitlementPlan = PLANS.find(plan => plan.code === (EntitlementIdentifier.Pro2 as string))
const ent2EntitlementPlan = PLANS.find(plan => plan.code === (EntitlementIdentifier.Ent2 as string))

type IosPaywallProps = {
  recommendedEntitlement?: EntitlementIdentifier
}

export default function IosPaywall({ recommendedEntitlement }: IosPaywallProps) {
  const insets = useSafeAreaInsets()
  const dispatch = useAppDispatch()
  const router = useRouter()
  const currentUser = useAppSelector(state => state.currentUser.user)

  const planInfo = currentUser?.billingAccount
    ? getBillingAccountPlanInfo(currentUser.billingAccount)
    : null
  const activePlanCode = planInfo?.activePlanCode as PlanCode | undefined
  const isCurrentEntitlementPro2 = activePlanCode === (EntitlementIdentifier.Pro2 as string)
  const isCurrentEntitlementEnt2 = activePlanCode === (EntitlementIdentifier.Ent2 as string)
  const isPlanAbovePaywall = isPlanCodeGtOther(activePlanCode, PlanCode.Ent2)

  const getInitialEntitlement = (): EntitlementIdentifier => {
    if (recommendedEntitlement) return recommendedEntitlement
    if (isCurrentEntitlementPro2) return EntitlementIdentifier.Ent2
    return EntitlementIdentifier.Pro2
  }

  const [packages, setPackages] = useState<PurchasesPackage[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedEntitlement, setSelectedEntitlement] = useState<EntitlementIdentifier>(getInitialEntitlement)
  const [selectedPackageIdentifier, setSelectedPackageIdentifier] = useState<PackageIdentifier | null>(null)

  const selectedEntitlementPlan = selectedEntitlement === EntitlementIdentifier.Pro2 ? pro2EntitlementPlan : ent2EntitlementPlan

  const loadOfferings = async () => {
    try {
      const offerings = await getOfferings()
      if (offerings?.availablePackages) {
        setPackages(offerings.availablePackages)
        if (selectedEntitlement === EntitlementIdentifier.Pro2) {
          setSelectedPackageIdentifier(PackageIdentifier.Pro2Yearly)
        } else {
          setSelectedPackageIdentifier(PackageIdentifier.Ent2Yearly)
        }
      }
    } catch (error) {
      console.error('Failed to load offerings:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOfferings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Update selected package when plan changes
    if (selectedEntitlement === EntitlementIdentifier.Pro2) {
      setSelectedPackageIdentifier(PackageIdentifier.Pro2Yearly)
    } else {
      setSelectedPackageIdentifier(PackageIdentifier.Ent2Yearly)
    }
  }, [selectedEntitlement])

  const [purchasing, setPurchasing] = useState(false)

  const handleRestore = async () => {
    const restored = await restorePurchases()
    if (restored) {
      await dispatch(fetchCurrentUserThunk())
      router.back()
    }
  }

  const handlePurchase = async () => {
    const isFreePlan = activePlanCode === PlanCode.Free
    const subscriptionSource = currentUser?.billingAccount?.planPackage?.planUpgradeStatus?.source
    const isApple = subscriptionSource === SubscriptionSource.Apple

    if ((!isFreePlan && !isApple) || isPlanAbovePaywall) { return }

    const selectedPackage = packages.find(p => p.identifier === selectedPackageIdentifier)

    if (!selectedPackage) return

    setPurchasing(true)
    try {
      const result = await purchasePackage(selectedPackage)
      if (result.success) {
        await dispatch(fetchCurrentUserThunk())
        router.back()
        return
      }

      if (result.needsRestore) {
        Alert.alert(
          '无法完成购买',
          '该 Apple ID 已在其他账号中订阅过此服务，请使用原账号登录，或联系客服处理。'
        )
      }
    } catch (error) {
      console.error('Purchase failed:', error)
    } finally {
      setPurchasing(false)
    }
  }

  const monthlyPackageId = selectedEntitlement === EntitlementIdentifier.Ent2 ? PackageIdentifier.Ent2Monthly : null
  const yearlyPackageId = selectedEntitlement === EntitlementIdentifier.Pro2 ? PackageIdentifier.Pro2Yearly : PackageIdentifier.Ent2Yearly

  const monthlyPackage = packages.find(p => p.identifier === monthlyPackageId)
  const yearlyPackage = packages.find(p => p.identifier === yearlyPackageId)

  const savingTips = selectedPackageIdentifier === yearlyPackageId && monthlyPackage && selectedEntitlementPlan?.supportMonthlyByDefault ? selectedEntitlementPlan?.perYearSavingTips : undefined

  return (
    <ScrollView
      contentInsetAdjustmentBehavior="automatic"
      className="bg-background-0"
      contentContainerStyle={Platform.OS === 'android' ? { paddingBottom: insets.bottom } : undefined}
    >
      <View className="p-5">
        <Text className="text-xl mb-6 text-label">
          根据你的需求，选择最佳方案
        </Text>

        {/* Plan Tabs */}
        <View className="flex-row bg-secondary-system-background rounded-3xl p-1 mb-6">
          <Pressable
            className={`flex-1 py-3 rounded-[20px] ${selectedEntitlement === EntitlementIdentifier.Pro2 ? 'bg-system-background' : ''}`}
            onPress={() => setSelectedEntitlement(EntitlementIdentifier.Pro2)}
          >
            <Text className="text-center font-semibold text-base text-label">
              专业版{isCurrentEntitlementPro2 ? '（当前）' : ''}
            </Text>
          </Pressable>
          <Pressable
            className={`flex-1 py-3 rounded-[20px] ${selectedEntitlement === EntitlementIdentifier.Ent2 ? 'bg-indigo-500' : ''}`}
            onPress={() => setSelectedEntitlement(EntitlementIdentifier.Ent2)}
          >
            <Text className={`text-center font-semibold text-base ${selectedEntitlement === EntitlementIdentifier.Ent2 ? 'text-white' : 'text-label'}`}>
              专业增强版{isCurrentEntitlementEnt2 ? '（当前）' : ''}
            </Text>
          </Pressable>
        </View>

        {/* Features */}
        <View className="mb-6">
          {selectedEntitlementPlan?.features.map((feature, index) => (
            <View key={index} className="flex-row items-center mb-2.5">
              <IconSymbol name="checkmark.circle.fill" size={22} color="#6366f1" style={{ marginRight: 10 }} />
              <Text className="text-base flex-1 text-label">{feature.text}</Text>
            </View>
          ))}
        </View>


        {/* Pricing Options */}
        <View className="flex-row gap-3 mb-6">
          {monthlyPackageId && monthlyPackage && (
            <PricingOption
              title="月付"
              price={<Text><Text className="text-lg font-bold text-label">{`${monthlyPackage.product.pricePerMonthString}`}</Text> <Text className="text-sm text-secondary-label">/月</Text></Text>}
              selected={selectedPackageIdentifier === monthlyPackageId}
              onPress={() => setSelectedPackageIdentifier(monthlyPackageId)}
            />
          )}

          {yearlyPackage && (
            <PricingOption
              title="年付"
              price={<Text><Text className="text-lg font-bold text-label">{`${yearlyPackage.product.priceString}`}</Text> <Text className="text-sm text-secondary-label">/年</Text></Text>}
              selected={selectedPackageIdentifier === yearlyPackageId}
              onPress={() => setSelectedPackageIdentifier(yearlyPackageId)}
              savingBadge={savingTips}
            />
          )}
        </View>

        {/* Purchase Button */}
        <Pressable
          className={`py-4 rounded-3xl mb-4 ${loading || purchasing ? 'bg-indigo-300' : 'bg-indigo-500'}`}
          onPress={handlePurchase}
          disabled={loading || purchasing}
        >
          {purchasing ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text className="text-white text-base font-bold text-center">
              继续
            </Text>
          )}
        </Pressable>


        {/* Footer Links */}
        <View className="flex-row justify-center gap-6">
          <Pressable onPress={handleRestore}>
            <Text className="text-sm text-secondary-label">恢复购买</Text>
          </Pressable>
          <Pressable onPress={() => router.push('/profile/terms')}>
            <Text className="text-sm text-secondary-label">条款</Text>
          </Pressable>
          <Pressable onPress={() => router.push('/profile/privacy')}>
            <Text className="text-sm text-secondary-label">隐私</Text>
          </Pressable>
        </View>

        <Text className="text-xs text-tertiary-label text-center mt-4">
          订阅到期后自动续费。如需取消，请在到期前 24 小时前往「iPhone 设置 → Apple ID → 订阅」中操作
        </Text>
      </View>
    </ScrollView>
  )
}
